import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import {
  useQueryParams,
  withDefault,
  NumberParam
} from 'use-query-params';

import { rem } from '../utilities/style';
import { ReactComponent as IconSocialLinkedin } from '../images/icons/social-linkedin.svg';
import { headers, body } from '../styles/type';
import { containerStyles } from '../styles/layout';
import { colors } from '../theme/colors';
import { media } from '../theme/media';
import { createOptionMap } from '../utilities/misc';
import { DEFAULT_POSTS_PER_PAGE } from '../constants';

import AnchorElement from '../components/AnchorElement';
import ArticleCard from '../components/ArticleCard';
import Breadcrumbs from '../components/Breadcrumbs';
import Image from '../components/Image';
import Layout from '../components/Layout';
import Link from '../components/Link';
import NoResults from '../components/NoResults';
import PageHead from '../components/PageHead';
import Pagination from '../components/Pagination';
import TextRenderer from '../components/TextRenderer';

const PageWrapper = styled.div`
  background-color: ${colors.blue['50']};
`;

const Container = styled.div`
  ${containerStyles.regular}
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${media('>small')} {
    display: flex;
    padding-top: ${rem(80)};
    padding-bottom: ${rem(80)};
  }
`;

const AuthorDetails = styled.div`
  flex: 0 1 ${rem(413)};
  margin-bottom: ${rem(64)};

  ${media('>medium')} {
    margin-bottom: 0;
  }
`;

const PostsWrapper = styled.div`
  flex: 1 1 auto;

  ${media('>small')} {
    padding: 0 min(${rem(113)}, 10%);
  }
`;

const AuthorName = styled.h1`
  ${headers.h3}
  margin-top: 0;
`;

const AuthorTitle = styled.p`
  ${body.regular};
  color: ${colors.gray[600]};
  margin-bottom: ${rem(48)};
`;

const AuthorBio = styled.p`
  ${body.regular}
  font-size: ${rem(18)};
  color: ${colors.gray[600]};
  margin-bottom: ${rem(48)};
`;

const AuthorExpertise = styled.div`
  margin-top: ${rem(20)};
  font-size: ${rem(18)};
`;

const PostsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(200)}, 1fr));
  gap: ${rem(40)};
`;

const PostsTitle = styled.h2`
  ${headers.h3}
  margin-bottom: ${rem(40)};
`;

const ExpertiseTitle = styled.h3`
  ${headers.h4}
  margin-bottom: ${rem(16)};
`;

const ExpertiseList = styled.ul`
  ${body.ul}
`;

const ExpertiseItem = styled.li`
  margin-bottom: ${rem(8)};
`;

const ExpertiseLinkText = styled.span`
  color: ${colors.blue[800]};
  text-decoration: underline;

  &:hover {
    color: ${colors.blue[500]};
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  max-width: max-content;
  margin-bottom: ${rem(8)};
`;

const AvatarImage = styled(Image)`
  width: ${rem(120)};
  height: ${rem(120)};
  border-radius: 50%;
`;

const SocialLink = styled.a`
  transition: color .2s ease, background .2s ease;
  display: block;
  border-radius: 50%;
  padding: ${rem(4)};
  background-color: ${colors.blue[800]};
  color: ${colors.white};
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;


  &:focus,
  &:hover {
    background: ${colors.blue[500]};
  }
`;

const Author = ({ data, pageContext }) => {
  const options = createOptionMap(data.allContentfulOptionSet.nodes);
  const author = data.contentfulAuthor;
  const posts = data.allContentfulBlog.nodes;
  const name = `${author.firstName} ${author.lastName}`;
  const relatedArticlesTitle = options['author_detail.related_articles_title']
    .replace('{{name}}', name);
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
  });
  const totalPages = Math.ceil(posts.length / DEFAULT_POSTS_PER_PAGE);

  const pagedPosts = useMemo(() => posts.slice(
    (currentPage - 1) * DEFAULT_POSTS_PER_PAGE,
    currentPage * DEFAULT_POSTS_PER_PAGE
  ), [currentPage, posts]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setQuery((prevQuery) => ({
      ...prevQuery,
      page: page !== 1 ? page : undefined
    }));
  };

  useEffect(() => {
    setCurrentPage(query.page);
    if (query.page > 1) {
      window.location.hash = 'author-posts';
    }
  }, [query]);

  return (
    <Layout language={pageContext.language}>
      <Breadcrumbs
        items={[
          {
            title: options['author_detail.breadcrumb_text'],
            url: '/blog',
            backButton: true
          },
          {
            title: name,
            current: true
          }
        ]}
      />
      <PageWrapper>
        <Container>
          <AuthorDetails itemScope itemType="https://schema.org/Person">
            {author.image && (
              <AvatarWrapper>
                <AvatarImage
                  itemProp="image"
                  image={author.image}
                  alt={author.image.description}
                />
                {author.linkedInUrl && (
                  <SocialLink
                    href={author.linkedInUrl}
                    target="_blank"
                    itemProp="sameAs"
                    aria-label="LinkedIn"
                  >
                    <IconSocialLinkedin
                      title="LinkedIn"
                      width={20}
                      height={20}
                    />
                  </SocialLink>
                )}
              </AvatarWrapper>
            )}
            {name && (
              <AuthorName itemProp="name">
                {name}
              </AuthorName>
            )}
            {author.jobTitle && (
              <AuthorTitle itemProp="jobTitle">
                <TextRenderer text={author.jobTitle} />
              </AuthorTitle>
            )}
            {author.bio && (
              <AuthorBio itemProp="description">
                <TextRenderer text={author.bio.bio} />
              </AuthorBio>
            )}
            {author.expertise && author.expertise.length > 0 && (
              <AuthorExpertise>
                <ExpertiseTitle>
                  {options['author_detail.expertise_title']}
                </ExpertiseTitle>
                <ExpertiseList>
                  {author.expertise.map((expertise) => (
                    <ExpertiseItem key={expertise}>
                      <Link to={`/blog?search=${encodeURIComponent(expertise)}`}>
                        <ExpertiseLinkText itemProp="knowsAbout">
                          {expertise}
                        </ExpertiseLinkText>
                      </Link>
                    </ExpertiseItem>
                  ))}
                </ExpertiseList>
              </AuthorExpertise>
            )}
          </AuthorDetails>
          <PostsWrapper>
            <AnchorElement id="author-posts" />
            <PostsTitle>
              {relatedArticlesTitle}
            </PostsTitle>
            {pagedPosts && pagedPosts.length > 0 ? (
              <PostsList>
                {pagedPosts.map((post) => (
                  <ArticleCard
                    key={post.id}
                    category={post.blogCategory ? post.blogCategory[0].title : null}
                    title={post.page ? post.page[0].title : null}
                    link={post.page ? `/blog/${post.page[0].slug}` : null}
                    image={post.image?.gatsbyImageData}
                    imageAlt={post.image?.description || null}
                  />
                ))}
              </PostsList>
            ) : (
              <NoResults />
            )}
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onChange={handlePageChange}
            />
          </PostsWrapper>
        </Container>
      </PageWrapper>
    </Layout>
  );
};

export const Head = ({ data, pageContext }) => {
  const author = data.contentfulAuthor;
  const name = `${author.firstName} ${author.lastName}`;
  const seo = {
    language: pageContext.language,
    title: name,
    image: author.ogImage,
    canonicalPath: `/authors/${author.slug}`
  };

  return (
    <PageHead
      language={pageContext.language}
      title={name}
      seo={seo}
      updatedAt={author.updatedAt}
    />
  );
};

export const query = graphql`
  query SingleAuthor($id: String!, $language: String!) {
    allContentfulOptionSet(
      filter: {name: {eq: "author_detail"}, node_locale: {eq: $language}}
    ) {
      nodes {
        ...OptionSetFragment
      }
    }
    contentfulAuthor(id: {eq: $id}) {
      updatedAt
      slug
      firstName
      lastName
      jobTitle
      image {
        gatsbyImageData(width: 120, height: 120, quality: 75, resizingBehavior: FILL)
        description
      }
      ogImage: image {
        gatsbyImageData(width: 1200, height: 630)
        description
        file {
          contentType
        }
      }
      bio {
        bio
      }
      expertise
      linkedInUrl
    }
    allContentfulBlog(filter: {author: {id: {eq: $id}, node_locale: {eq: $language}}}) {
      nodes {
        id
        page {
          slug
          title
        }
        blogCategory {
          title
        }
        image {
          gatsbyImageData(width: 300, height: 200, quality: 75, resizingBehavior: FILL)
          description
        }
      }
    }
  }
`;

export default Author;
